import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Reward types - 100% GAGNANT (no lose)
const REWARDS = {
  credit3: { type: "credit", amount: 3, label: "3€", color: "text-pink-600" },
  treatment: { type: "treatment", amount: 1, label: "🧴", color: "text-purple-600" },
  credit5: { type: "credit", amount: 5, label: "5€", color: "text-pink-600" },
  credit10: { type: "credit", amount: 10, label: "10€", color: "text-pink-700" },
};

// Generate random reward based on probabilities (NO LOSE - 100% gagnant)
// 3€: ~47%, Treatment: ~33%, 5€: ~20%
const generateRandomRewardNoJackpot = () => {
  const rand = Math.random() * 100;
  if (rand < 47) return REWARDS.credit3;
  if (rand < 80) return REWARDS.treatment;
  return REWARDS.credit5;
};

// Generate rewards ensuring EXACTLY one 10€ (jackpot) - 100% GAGNANT
const generateAllRewards = (count) => {
  // Start with one of each guaranteed (including exactly ONE 10€)
  const rewards = [
    REWARDS.credit3,
    REWARDS.credit3,
    REWARDS.treatment,
    REWARDS.treatment,
    REWARDS.credit5,
    REWARDS.credit10,  // Exactly ONE jackpot - never more!
  ];
  
  // Fill the rest randomly (WITHOUT 10€ - already have exactly one)
  while (rewards.length < count) {
    rewards.push(generateRandomRewardNoJackpot());
  }
  
  // Shuffle the array
  for (let i = rewards.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [rewards[i], rewards[j]] = [rewards[j], rewards[i]];
  }
  
  return rewards;
};

// 14 petal positions (added 2 more for "lose" petals)
const PETAL_POSITIONS = [
  { top: "8%", left: "50%", rotate: -10 },
  { top: "15%", left: "28%", rotate: 20 },
  { top: "12%", left: "72%", rotate: -25 },
  { top: "28%", left: "15%", rotate: 35 },
  { top: "25%", left: "50%", rotate: -5 },
  { top: "28%", left: "85%", rotate: -35 },
  { top: "42%", left: "25%", rotate: 15 },
  { top: "40%", left: "60%", rotate: -20 },
  { top: "42%", left: "82%", rotate: -40 },
  { top: "55%", left: "18%", rotate: 30 },
  { top: "52%", left: "50%", rotate: 5 },
  { top: "55%", left: "78%", rotate: -30 },
  { top: "18%", left: "42%", rotate: 12 },  // New petal 13
  { top: "35%", left: "70%", rotate: -15 }, // New petal 14
];

// Petal component
const Petal = ({ index, reward, onClick, disabled, falling, revealed, isWinner, actualReward }) => {
  const pos = PETAL_POSITIONS[index];
  
  // Use actual reward from backend for the winner petal, otherwise use the generated reward
  const displayReward = (revealed && isWinner && actualReward) ? actualReward : reward;
  
  return (
    <button
      onClick={() => !disabled && !falling && !revealed && onClick(index)}
      disabled={disabled || falling || revealed}
      className={`absolute transition-all duration-700 ${falling ? "animate-petal-fall" : ""} ${disabled && !revealed ? "opacity-40 cursor-not-allowed" : revealed ? "cursor-default" : "cursor-pointer hover:scale-110"}`}
      style={{ 
        top: pos.top, 
        left: pos.left, 
        transform: `translateX(-50%) rotate(${pos.rotate}deg)`,
        filter: (disabled && !revealed) ? "grayscale(50%)" : revealed ? (isWinner ? "drop-shadow(0 0 20px rgba(255, 215, 0, 0.9))" : "drop-shadow(0 0 8px rgba(180, 180, 180, 0.5))") : "drop-shadow(0 0 12px rgba(255, 182, 193, 0.8))",
        opacity: revealed && !isWinner ? 0.6 : 1,
        zIndex: isWinner ? 10 : 1,
      }}
    >
      <svg width="50" height="65" viewBox="0 0 60 75" className={!revealed && !falling ? "animate-petal-float" : ""} style={{ animationDelay: `${index * 0.12}s` }}>
        <defs>
          <linearGradient id={`petalGrad${index}`} x1="0%" y1="0%" x2="100%" y2="100%">
            {revealed ? (
              isWinner ? (
                <>
                  <stop offset="0%" stopColor="#FFD700" />
                  <stop offset="50%" stopColor="#FFC107" />
                  <stop offset="100%" stopColor="#FF9800" />
                </>
              ) : (
                <>
                  <stop offset="0%" stopColor="#E0E0E0" />
                  <stop offset="50%" stopColor="#C8C8C8" />
                  <stop offset="100%" stopColor="#A8A8A8" />
                </>
              )
            ) : (
              <>
                <stop offset="0%" stopColor="#FFE4EC" />
                <stop offset="50%" stopColor="#FFB6C8" />
                <stop offset="100%" stopColor="#FF8FAB" />
              </>
            )}
          </linearGradient>
        </defs>
        <ellipse 
          cx="30" cy="40" rx="25" ry="35" 
          fill={`url(#petalGrad${index})`}
          stroke={revealed ? (isWinner ? "#FFD700" : "#888") : "#FF69B4"}
          strokeWidth={isWinner ? "2" : "1"}
        />
        <ellipse cx="25" cy="30" rx="10" ry="15" fill="rgba(255,255,255,0.35)"/>
      </svg>
      
      <span className={`absolute inset-0 flex items-center justify-center font-bold ${revealed ? `text-base ${displayReward.color}` : "text-white text-xl"}`} style={{ textShadow: revealed ? "none" : "0 2px 4px rgba(0,0,0,0.3)" }}>
        {revealed ? displayReward.label : "?"}
      </span>
      
      {revealed && isWinner && (
        <span className="absolute -top-1 -right-1 bg-yellow-400 text-yellow-900 text-xs font-bold w-5 h-5 rounded-full shadow flex items-center justify-center">
          ✓
        </span>
      )}
    </button>
  );
};

// Confetti component
const Confetti = ({ show }) => {
  if (!show) return null;
  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      {[...Array(60)].map((_, i) => (
        <div
          key={i}
          className="absolute animate-confetti"
          style={{
            left: `${Math.random() * 100}%`,
            top: "-20px",
            animationDelay: `${Math.random() * 2}s`,
            animationDuration: `${2.5 + Math.random() * 2}s`
          }}
        >
          <div 
            className="rounded-full"
            style={{
              width: `${8 + Math.random() * 8}px`,
              height: `${8 + Math.random() * 8}px`,
              backgroundColor: ['#FFB6C8', '#FF69B4', '#FFE4EC', '#FF8FAB', '#FFC0CB'][i % 5]
            }}
          />
        </div>
      ))}
    </div>
  );
};

const SakuraTree = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [fallingPetal, setFallingPetal] = useState(null);
  const [result, setResult] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [winnerIndex, setWinnerIndex] = useState(null);
  const [actualWinReward, setActualWinReward] = useState(null); // Real reward from backend
  const isDrawingRef = useRef(false); // Prevent double-click race condition
  
  // Generate rewards for all 12 petals (at least one of each type)
  const petalRewards = useMemo(() => generateAllRewards(14), []);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API}/customer/sakura-tree/status`, { withCredentials: true });
      setStatus(response.data);
      
      // Reset game state if eligible for a new draw
      if (response.data.eligible) {
        setRevealed(false);
        setResult(null);
        setWinnerIndex(null);
        setFallingPetal(null);
        setActualWinReward(null);
        isDrawingRef.current = false;
      }
    } catch (error) {
      console.error("Error fetching sakura status:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePetalClick = async (petalIndex) => {
    // Triple protection against double-clicks
    if (!status?.eligible || playing || revealed || isDrawingRef.current) return;
    
    // Lock immediately with ref (survives re-renders)
    isDrawingRef.current = true;
    setPlaying(true);
    setFallingPetal(petalIndex);
    setWinnerIndex(petalIndex);
    
    await new Promise(resolve => setTimeout(resolve, 900));
    
    try {
      const response = await axios.post(`${API}/customer/sakura-tree/draw`, {}, { withCredentials: true });
      setResult(response.data);
      
      // Convert backend reward to display format
      const backendReward = response.data.reward_type === "treatment" 
        ? REWARDS.treatment
        : response.data.amount === 10 
          ? REWARDS.credit10
          : response.data.amount === 5 
            ? REWARDS.credit5
            : REWARDS.credit3;
      setActualWinReward(backendReward);
      
      setShowConfetti(true);
      if (response.data.reward_type === "treatment") {
        toast.success("🧴 Tu as gagné 1 traitement additionnel offert !");
      } else {
        toast.success(`🎉 Tu as gagné ${response.data.amount}€ !`);
      }
      setTimeout(() => setShowConfetti(false), 4500);
      
      setTimeout(() => {
        setRevealed(true);
        setFallingPetal(null);
      }, 500);
      
      // Re-fetch status to check for remaining bonus draws
      setTimeout(() => {
        fetchStatus();
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du tirage");
      setFallingPetal(null);
      setWinnerIndex(null);
    } finally {
      setPlaying(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-2xl p-8 text-center bg-gradient-to-b from-pink-100 to-pink-50">
        <div className="animate-pulse text-pink-500">Chargement du cerisier...</div>
      </div>
    );
  }

  return (
    <>
      <Confetti show={showConfetti} />
      
      <div className="rounded-2xl overflow-hidden shadow-xl border border-pink-200">
        {/* Header */}
        <div className="bg-gradient-to-r from-pink-400 via-pink-500 to-pink-400 text-white py-3 px-4 text-center">
          <h3 className="text-xl font-bold flex items-center justify-center gap-2">
            🌸 Le Cerisier Sakura
          </h3>
          <p className="text-pink-100 text-sm">Tentez votre chance chaque mois !</p>
          <p className="text-pink-200 text-xs mt-1">⚠️ Les tirages ne sont pas cumulables</p>
        </div>
        
        {/* Tree container */}
        <div 
          className="relative h-72 w-full bg-cover bg-center bg-no-repeat"
          style={{ 
            backgroundImage: "url('https://static.prod-images.emergentagent.com/jobs/2865d04f-1753-4ac2-b4d4-74d332d7faa7/images/4d70de862e8e977ec87f55d71a4c8846bf183b8415b0fcb90fddef0aa560df92.png')",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-white/40 via-transparent to-pink-100/20" />
          
          {/* Petals */}
          <div className="absolute inset-0">
            {PETAL_POSITIONS.map((_, i) => (
              <Petal
                key={i}
                index={i}
                reward={petalRewards[i]}
                onClick={handlePetalClick}
                disabled={!status?.eligible}
                falling={fallingPetal === i}
                revealed={revealed}
                isWinner={winnerIndex === i}
                actualReward={winnerIndex === i ? actualWinReward : null}
              />
            ))}
          </div>
        </div>
        
        {/* Message & Info */}
        <div className="bg-gradient-to-b from-pink-50 to-white p-4">
          {/* Spending Progress Bar */}
          {status && typeof status.monthly_spending !== 'undefined' && !status.eligible && !status.already_played && (
            <div className="mb-4 p-3 bg-white rounded-xl border border-pink-200">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-slate-700">Progression ce mois</span>
                <span className="text-sm font-bold text-pink-600">
                  {status.monthly_spending?.toFixed(2)}€ / {status.spending_goal}€
                </span>
              </div>
              <div className="h-3 bg-pink-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-pink-400 to-pink-600 rounded-full transition-all duration-500"
                  style={{ width: `${status.spending_progress || 0}%` }}
                />
              </div>
              {status.monthly_spending < status.spending_goal && (
                <p className="text-xs text-pink-500 mt-2 text-center">
                  Encore {(status.spending_goal - status.monthly_spending).toFixed(2)}€ pour débloquer ton tirage !
                </p>
              )}
            </div>
          )}
          
          <p className="text-center text-pink-600 font-medium mb-3">{status?.message}</p>
          
          {/* Credits display */}
          <div className="flex justify-center gap-3 text-sm mb-3 flex-wrap">
            {(status?.prepaid_credit > 0 || result?.reward_type === "credit") && (
              <span className="bg-pink-100 text-pink-700 px-3 py-1 rounded-full">
                💰 Crédit: <strong>{result?.new_balance || status?.prepaid_credit || 0}€</strong>
              </span>
            )}
            {(status?.free_treatments > 0 || result?.reward_type === "treatment") && (
              <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full">
                🧴 Traitements: <strong>{result?.new_treatments || status?.free_treatments || 0}</strong>
              </span>
            )}
          </div>
          
          {/* Result display */}
          {result && (
            <div className="p-4 bg-white rounded-xl border-2 border-pink-300 text-center animate-bounce-in shadow-lg">
              {result.reward_type === "treatment" ? (
                <>
                  <p className="text-3xl font-bold text-purple-600">🧴 +1 Traitement</p>
                  <p className="text-sm text-slate-600 mt-1">Traitement additionnel offert !</p>
                  <p className="text-xs text-slate-500 mt-2">Total en stock : {result.new_treatments}</p>
                </>
              ) : (
                <>
                  <p className="text-3xl font-bold text-pink-600">🎉 +{result.amount}€</p>
                  <p className="text-sm text-slate-600 mt-1">Ajouté à ton crédit prépayé !</p>
                  <p className="text-xs text-slate-500 mt-2">Nouveau solde : {result.new_balance}€</p>
                </>
              )}
            </div>
          )}
          
          {/* Revealed info */}
          {revealed && (
            <p className="text-center text-xs text-slate-500 mt-3 bg-slate-50 rounded-lg p-2">
              ✨ Les autres pétales montrent ce que tu aurais pu gagner !
            </p>
          )}
          
          {/* Already played - but check for bonus draws */}
          {status?.already_played && !result && !status?.eligible && (
            <div className="p-3 bg-pink-100 rounded-lg text-center">
              <p className="text-pink-700 font-medium">
                {status.last_reward_type === "lose"
                  ? "😢 Tu n'as rien gagné ce mois-ci..."
                  : status.last_reward_type === "treatment" 
                    ? "🧴 Tu as gagné 1 traitement ce mois-ci !"
                    : `🎉 Tu as gagné ${status.last_win}€ ce mois-ci !`
                }
              </p>
              <p className="text-xs text-pink-600 mt-1">Reviens le mois prochain pour rejouer 🌸</p>
            </div>
          )}
          
          {/* Bonus draws available message */}
          {status?.total_available_draws > 0 && status?.eligible && !result && (
            <div className="p-3 bg-gradient-to-r from-yellow-100 to-pink-100 rounded-lg text-center border border-yellow-300">
              <p className="text-pink-700 font-medium">
                🎁 Tu as <span className="text-lg font-bold text-pink-600">{status.total_available_draws}</span> tirage(s) disponible(s) !
              </p>
              <p className="text-xs text-pink-600 mt-1">
                {status.total_available_draws > 1 ? "(1 par mois max) " : ""}Clique sur un pétale pour jouer 🌸
              </p>
            </div>
          )}
          
          {/* Info text */}
          {status?.eligible && !result && !status?.total_available_draws && (
            <p className="text-center text-xs text-slate-500 mt-3">
              Cliquez sur un pétale pour découvrir votre récompense !
            </p>
          )}
          
          {/* Not eligible */}
          {!status?.eligible && !status?.already_played && (
            <div className="mt-3 p-4 bg-gradient-to-r from-amber-50 to-pink-50 rounded-lg border border-amber-200">
              <p className="text-center text-amber-700 mb-2">
                <strong>🎁 Gagnez jusqu'à 10€ de crédit !</strong>
              </p>
              <p className="text-center text-xs text-amber-600">
                Réservez ce mois-ci pour débloquer le jeu le mois prochain.<br/>
                <span className="text-pink-600">Crédits • Traitements offerts</span>
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* CSS Animations */}
      <style>{`
        @keyframes petal-fall {
          0% { transform: translateX(-50%) translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateX(-50%) translateY(320px) rotate(540deg); opacity: 0; }
        }
        @keyframes petal-float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-4px) rotate(2deg); }
        }
        @keyframes confetti {
          0% { transform: translateY(0) rotate(0deg) scale(1); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg) scale(0.5); opacity: 0; }
        }
        @keyframes bounce-in {
          0% { transform: scale(0.3); opacity: 0; }
          50% { transform: scale(1.05); }
          70% { transform: scale(0.95); }
          100% { transform: scale(1); opacity: 1; }
        }
        .animate-petal-fall { animation: petal-fall 0.9s ease-in forwards; }
        .animate-petal-float { animation: petal-float 3s ease-in-out infinite; }
        .animate-confetti { animation: confetti 3s ease-out forwards; }
        .animate-bounce-in { animation: bounce-in 0.6s ease-out forwards; }
      `}</style>
    </>
  );
};

export default SakuraTree;
